import { clearPackageRequestKey, packageRequestKey, paymentLink, rememberPackageCheckout } from 'src/utils/packageCheckout';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Switch,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Tooltip,
    LinearProgress,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { IconRefresh } from '@tabler/icons-react';

import { DesktopLayout, LayoutPaper } from 'src/layout';
import AutoTopUpChoice from 'src/components/AutoTopUpChoice';
import LeadPackageCashback from 'src/components/LeadPackageCashback';
import { PromoTicketInput } from 'src/components/TicketField';
import { ROUTES_URL } from 'src/constants/routes';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { getDailyLeadsStats } from 'src/services/StatsService';
import EventService from 'src/services/EventService';
import SubscriptionsService from 'src/services/SubscriptionsService';
import formatNumber from 'src/utils/formatNumber';
import { streamDetailsPath, streamIdFromPath } from 'src/utils/streamUrl';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useUserStore from 'src/stores/user';
import {
    formatStreamDate,
    formatStreamShortDate,
    getStreamApprovedDeliveries,
    getStreamBalance,
    getStreamDisplayName,
    getStreamPackageSize,
    getStreamStatus,
    getStreamStatusColor,
    getStreamTotalSize,
    hasActiveStreamAccess,
    shouldOpenStreamDetails,
} from 'src/utils/leadStreams';

import StreamWorkspaceSections from './StreamWorkspaceSections';
import StreamManagerFunnel from './StreamManagerFunnel';
import StreamPackagePicker from './StreamPackagePicker';
import StreamBillingChoice from './StreamBillingChoice';
import AnnualUnlimitedCard from './AnnualUnlimitedCard';
import StreamPurchaseHistory, { purchasesForStream } from './StreamPurchaseHistory';
import StreamPlanBadge from 'src/components/StreamPlanBadge';
import StreamActivityCalendar, { leadCountLabel, summarizeActivity } from './StreamActivityCalendar';
import { StreamPageSections, StreamSection, StreamSectionNavigation, StickyStreamHeader } from './StreamPageSections';

import type { PageSection, StreamNavigationHandle } from './StreamPageSections';

import type {
    LeadPackagePrice,
    LeadPackagePurchase,
    LeadPricingOffer,
    MonthlyLeadStatus,
    Stream,
} from 'src/services/LeadStreamsService/agent';
import type { DailyStats } from 'src/types/Stats';

const CARD_RADIUS = '8px';
const CARD_BORDER = 'var(--lm-border)';
const CARD_SHADOW = 'none';

const cardSx = {
    backgroundColor: 'var(--lm-surface)',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: CARD_RADIUS,
    boxShadow: CARD_SHADOW,
} as const;

type PerformanceRow = { key: string; label: string; count: number };
const DEFAULT_PERFORMANCE_PERIOD_DAYS = 30;
const PERFORMANCE_CALENDAR_DAYS = 365;

function parseMoney(value?: string | number | null) {
    const numberValue = typeof value === 'number' ? value : parseFloat(String(value || '0'));
    return Number.isFinite(numberValue) ? numberValue : 0;
}

function formatCurrency(value: number, maximumFractionDigits = 0) {
    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: maximumFractionDigits > 0 && !Number.isInteger(rounded) ? 2 : 0,
        maximumFractionDigits,
        style: 'currency',
        currency: 'RUB',
    }).format(rounded);
}

function getPackageTotal(price: LeadPackagePrice | null) {
    if (!price) {
        return 0;
    }

    return parseMoney(price.price_per_lead) * price.package_size;
}

function getPerformanceRows(stats: DailyStats[] = [], days: number): PerformanceRow[] {
    const statByDate = new Map(stats.map((item) => [dayjs(item.date).format('YYYY-MM-DD'), Number(item.count) || 0]));

    return Array.from({ length: days }, (_, index) => {
        const date = dayjs().subtract(days - index - 1, 'day');
        const key = date.format('YYYY-MM-DD');

        return {
            key,
            label: date.format('DD.MM'),
            count: statByDate.get(key) || 0,
        };
    });
}

function getUsagePercent(stream: Stream | null) {
    if (!stream) {
        return 0;
    }

    if (hasActiveStreamAccess(stream)) {
        const start = stream.access_activated_at ? new Date(stream.access_activated_at).getTime() : 0;
        const end = stream.access_expires_at ? new Date(stream.access_expires_at).getTime() : 0;
        const now = Date.now();

        if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
            return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
        }

        return 0;
    }

    const total = getStreamTotalSize(stream);
    if (!total) {
        return 0;
    }

    return Math.min(100, Math.max(0, Math.round((getStreamApprovedDeliveries(stream) / total) * 100)));
}

function getDayWord(value: number) {
    const absValue = Math.abs(value);
    const lastTwo = absValue % 100;
    const last = absValue % 10;

    if (lastTwo >= 11 && lastTwo <= 14) {
        return 'дней';
    }

    if (last === 1) {
        return 'день';
    }

    if (last >= 2 && last <= 4) {
        return 'дня';
    }

    return 'дней';
}

function getRunoutForecast(stream: Stream | null, rows: PerformanceRow[]) {
    if (!stream) {
        return '';
    }

    const status = getStreamStatus(stream);
    if (status.key === 'pending') return 'Поиск начнётся после оплаты пакета.';

    if (status.key === 'paused') {
        return 'Расход остановлен';
    }

    if (status.key === 'ended') {
        return 'Пакет закончился';
    }

    if (hasActiveStreamAccess(stream)) {
        const expiresAt = formatStreamDate(stream.access_expires_at);
        return expiresAt ? `Безлимит до ${expiresAt}` : 'Безлимит активен';
    }

    const balance = getStreamBalance(stream);
    if (balance <= 0) {
        return 'Баланс закончился';
    }

    const createdAt = stream.created_at ? dayjs(stream.created_at).startOf('day') : null;
    const activeRows = createdAt ? rows.filter((row) => !dayjs(row.key).isBefore(createdAt, 'day')) : rows;
    const forecastRows = activeRows.length > 0 ? activeRows : rows;
    const deliveredInPeriod = forecastRows.reduce((sum, row) => sum + row.count, 0);
    if (deliveredInPeriod <= 0) {
        return 'Расхода пока нет';
    }

    const averagePerDay = deliveredInPeriod / Math.max(forecastRows.length, 1);
    const daysLeft = Math.max(1, Math.ceil(balance / averagePerDay));

    return `При текущем темпе пакет закончится через ${formatNumber(daysLeft)} ${getDayWord(daysLeft)}`;
}

function getMainActionLabel(stream: Stream | null) {
    if (!stream) {
        return 'Вернуться к запуску';
    }

    const status = getStreamStatus(stream);
    if (status.key === 'pending') return 'Запустить поток';

    if (status.key === 'paused') {
        return 'Возобновить';
    }

    if (status.key === 'active' && getStreamApprovedDeliveries(stream) > 0) {
        return 'Открыть лиды';
    }

    return 'Пополнить';
}

function UsageOverviewCard({
    stream,
    currentPackageSize,
    usagePercent,
    forecast,
    children,
}: {
    stream: Stream;
    currentPackageSize: number | null;
    usagePercent: number;
    forecast: string;
    children?: React.ReactNode;
}) {
    const status = getStreamStatus(stream);
    const isUnlimited = hasActiveStreamAccess(stream);
    const balance = getStreamBalance(stream);
    const approvedDeliveries = getStreamApprovedDeliveries(stream);
    const packageSize = getStreamPackageSize(stream);
    const expiresAt = formatStreamShortDate(stream.access_expires_at);
    const statusColor = getStreamStatusColor(status.key);
    const displayName = getStreamDisplayName(stream);
    const categoryName = stream.category.name;
    const shouldShowCategory = Boolean(categoryName && categoryName !== displayName);
    const mainLabel = isUnlimited ? (expiresAt ? `∞ до ${expiresAt}` : '∞') : `${formatNumber(balance)} осталось`;
    const helperLabel = isUnlimited
        ? `Осталось ${100 - usagePercent}% срока`
        : packageSize > 0
        ? `Получено ${formatNumber(approvedDeliveries)} из ${formatNumber(packageSize)}`
        : 'Баланс потока';

    return (
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={1.5}>
                <Stack
                    direction="row"
                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                    justifyContent="space-between"
                    gap={1.5}
                >
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        gap={1.25}
                        sx={{ minWidth: 0 }}
                    >
                        <Box
                            sx={{
                                alignItems: 'center',
                                backgroundColor: 'var(--lm-canvas)',
                                border: `1px solid ${CARD_BORDER}`,
                                borderRadius: '8px',
                                display: 'flex',
                                flexShrink: 0,
                                height: 42,
                                justifyContent: 'center',
                                width: 42,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: 22, lineHeight: 1 }}
                                aria-hidden="true"
                            >
                                {stream.category.icon || '📁'}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={0.8}
                            >
                                <Typography
                                    component="h1"
                                    color="text.primary"
                                    sx={{
                                        fontSize: { xs: 16, sm: 22 },
                                        fontWeight: 800,
                                        lineHeight: 1.15,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {displayName}
                                </Typography>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        border: `1px solid ${CARD_BORDER}`,
                                        borderRadius: '999px',
                                        display: 'inline-flex',
                                        gap: 0.7,
                                        minHeight: 25,
                                        px: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: statusColor,
                                            borderRadius: '50%',
                                            height: 7,
                                            width: 7,
                                        }}
                                    />
                                    <Typography sx={{ color: 'var(--lm-muted)', fontSize: 12.5, fontWeight: 600 }}>
                                        {status.key === 'pending' ? 'Не запущен' : status.label}
                                    </Typography>
                                </Box>
                                <StreamPlanBadge stream={stream} packageSize={currentPackageSize} />
                            </Stack>
                            {shouldShowCategory && (
                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, fontWeight: 500, mt: 0.5 }}>
                                    {categoryName}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    color: 'var(--lm-muted)',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    mt: 0.6,
                                }}
                            >
                                {forecast}
                            </Typography>
                        </Box>
                    </Stack>
                    {status.key !== 'pending' && (
                        <Box sx={{ flexShrink: 0, textAlign: { xs: 'left', sm: 'right' } }}>
                            <Typography
                                color="text.primary"
                                sx={{ fontSize: { xs: 16, sm: 22 }, fontWeight: 800, lineHeight: 1.2 }}
                            >
                                {mainLabel}
                            </Typography>
                            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 12.5, fontWeight: 600, mt: 0.35 }}>
                                {helperLabel}
                            </Typography>
                        </Box>
                    )}
                </Stack>
                {status.key !== 'pending' && (
                    <LinearProgress
                        variant="determinate"
                        value={100 - usagePercent}
                        aria-label={isUnlimited ? 'Остаток срока доступа' : 'Остаток лидов в пакете'}
                        aria-valuetext={isUnlimited ? helperLabel : `${balance} лидов осталось`}
                        sx={{
                            backgroundColor: 'var(--lm-border)',
                            borderRadius: 999,
                            height: 4,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'var(--lm-accent-ink)',
                                borderRadius: 999,
                            },
                        }}
                    />
                )}
                {children}
            </Stack>
        </Box>
    );
}

function LeadStreamDetailsSkeleton() {
    return (
        <Stack spacing={3}>
            <Skeleton
                height={42}
                sx={{ borderRadius: 1.2, maxWidth: 230 }}
                variant="rounded"
            />

            <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={1.4}>
                    <Skeleton
                        height={24}
                        sx={{ borderRadius: 1.2, maxWidth: 150 }}
                        variant="rounded"
                    />
                    <Skeleton
                        height={18}
                        sx={{ borderRadius: 1.2, maxWidth: 360 }}
                        variant="rounded"
                    />
                    <Skeleton
                        height={8}
                        sx={{ borderRadius: 999 }}
                        variant="rounded"
                    />
                </Stack>
            </Box>

            {[0, 1, 2].map((item) => (
                <Box
                    key={item}
                    sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}
                >
                    <Stack spacing={1.4}>
                        <Skeleton
                            height={24}
                            sx={{ borderRadius: 1.2, maxWidth: 190 }}
                            variant="rounded"
                        />
                        <Skeleton
                            height={item === 0 ? 154 : 86}
                            sx={{ borderRadius: 2 }}
                            variant="rounded"
                        />
                    </Stack>
                </Box>
            ))}
        </Stack>
    );
}

export function PerformanceChart({
    calendarRows,
    recentRows = [],
    isLoading,
    error,
    onRetry,
}: {
    calendarRows: PerformanceRow[];
    recentRows?: PerformanceRow[];
    isLoading: boolean;
    error?: boolean;
    onRetry?: () => void;
}) {
    const activity = summarizeActivity(calendarRows);
    const recentLeads = recentRows.reduce((sum, day) => sum + day.count, 0);
    const lastLead = [...calendarRows].reverse().find((day) => day.count > 0);
    const metrics = [
        { label: `Получено за ${calendarRows.length} дней`, value: leadCountLabel(activity.total) },
        { label: 'Лучший день', value: leadCountLabel(activity.bestDay) },
        { label: 'Текущая серия', value: `${activity.currentStreak} ${getDayWord(activity.currentStreak)}` },
        { label: 'Лучшая серия', value: `${activity.longestStreak} ${getDayWord(activity.longestStreak)}` },
    ];
    return (
        <StreamSection
            id="performance"
            title="Статистика"
        >
            {isLoading ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                    <CircularProgress
                        size={24}
                        aria-label="Загрузка статистики"
                    />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    action={<Button onClick={onRetry}>Повторить</Button>}
                >
                    Не удалось загрузить статистику
                </Alert>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                            border: '1px solid var(--lm-border)',
                            borderRadius: '8px',
                            mb: 3,
                            overflow: 'hidden',
                        }}
                    >
                        {metrics.map((metric, index) => (
                            <Box
                                key={metric.label}
                                sx={{
                                    minWidth: 0,
                                    px: { xs: 1.5, sm: 2 },
                                    py: 1.5,
                                    borderLeft: { md: index ? '1px solid var(--lm-border)' : 'none' },
                                    borderTop: { xs: index >= 2 ? '1px solid var(--lm-border)' : 'none', md: 'none' },
                                }}
                            >
                                <Typography sx={{ color: 'var(--lm-text)', fontSize: { xs: 16, sm: 18 }, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {metric.value}
                                </Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 12, lineHeight: 1.3 }}>
                                    {metric.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Stack direction="row" flexWrap="wrap" gap={{ xs: 1, sm: 3 }} sx={{ mb: 2.5, color: 'var(--lm-muted)' }}>
                        <Typography sx={{ fontSize: 13 }}>За 30 дней <Box component="strong" sx={{ color: 'var(--lm-text)' }}>{formatNumber(recentLeads)} лидов</Box></Typography>
                        {lastLead && <Typography sx={{ fontSize: 13 }}>Последний лид {lastLead.label}</Typography>}
                    </Stack>
                    <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, mb: 1.5 }}>
                        Активность лидов
                    </Typography>
                    <StreamActivityCalendar days={calendarRows} />
                </>
            )}
        </StreamSection>
    );
}

function StreamCheckoutPanel({
    activePrices,
    annualOffer,
    bonusPoints,
    isLoading,
    isPurchasing,
    isValidatingPromo,
    monthlyStatus,
    latestPaidPurchase,
    purchaseMode,
    onModeChange,
    onCancelMonthly,
    isCancellingMonthly,
    onPackageSelect,
    onPromoCodeChange,
    onPurchase,
    onUseBonusPointsChange,
    promoCode,
    promoDiscount,
    promoError,
    selectedPackageSize,
    stream,
    useBonusPoints,
}: {
    activePrices: LeadPackagePrice[];
    annualOffer: LeadPricingOffer | null;
    bonusPoints: number;
    isLoading: boolean;
    isPurchasing: boolean;
    isValidatingPromo: boolean;
    monthlyStatus: MonthlyLeadStatus;
    latestPaidPurchase: LeadPackagePurchase | null;
    purchaseMode: 'monthly' | 'one_time' | 'annual';
    onModeChange: (mode: 'monthly' | 'one_time' | 'annual') => void;
    onCancelMonthly: () => void;
    isCancellingMonthly: boolean;
    onPackageSelect: (packageSize: number) => void;
    onPromoCodeChange: (value: string) => void;
    onPurchase: () => void;
    onUseBonusPointsChange: (value: boolean) => void;
    promoCode: string;
    promoDiscount: number | null;
    promoError: string | null;
    selectedPackageSize: number | null;
    stream: Stream;
    useBonusPoints: boolean;
}) {
    const subscription = monthlyStatus.subscription;
    const pendingLaunch = getStreamStatus(stream).key === 'pending';
    const annualMode = purchaseMode === 'annual' && annualOffer !== null;
    const activeAnnualAccess = hasActiveStreamAccess(stream);
    const activeBalance = getStreamBalance(stream);
    const monthlyMode = !hasActiveStreamAccess(stream) && monthlyStatus.available && subscription?.status !== 'pending' && purchaseMode === 'monthly';
    const monthlyAllocationChange = monthlyMode && subscription?.status === 'active';
    const currentPlanSize = monthlyAllocationChange ? subscription.package_size : latestPaidPurchase?.package_size || stream.fixed_package_size || null;
    const currentPlanName = currentPlanSize === 50 ? 'Старт' : currentPlanSize === 100 ? 'Про' : currentPlanSize === 200 ? 'Бизнес' : currentPlanSize ? `${formatNumber(currentPlanSize)} лидов` : null;
    const protectedMonthlyUnit = (packageSize: number) => {
        if (subscription?.status !== 'active') return null;
        if (packageSize === subscription.package_size) return subscription.price_per_lead;
        return subscription.protected_prices?.find((price) => price.package_size === packageSize)?.price_per_lead || null;
    };
    const displayPrices = activePrices.map((price) => ({
        ...price,
        price_per_lead: monthlyMode
            ? protectedMonthlyUnit(price.package_size) || price.monthly_price_per_lead || price.price_per_lead
            : price.one_time_price_per_lead || price.price_per_lead,
    }));
    const selectedRawPrice = activePrices.find((price) => price.package_size === selectedPackageSize) || null;
    const monthlyUnit = parseMoney(selectedPackageSize ? protectedMonthlyUnit(selectedPackageSize) || selectedRawPrice?.monthly_price_per_lead || selectedRawPrice?.price_per_lead : null);
    const oneTimeUnit = parseMoney(selectedRawPrice?.one_time_price_per_lead || selectedRawPrice?.price_per_lead);
    const selectedPrice = displayPrices.find((price) => price.package_size === selectedPackageSize) || null;
    const currentUnit = parseMoney(monthlyAllocationChange ? subscription.price_per_lead : latestPaidPurchase?.price_per_lead);
    const selectedUnit = parseMoney(selectedPrice?.price_per_lead);
    const unitSaving = Math.max(0, currentUnit - selectedUnit);
    const selectedPlanName = selectedPackageSize === 50 ? 'Старт' : selectedPackageSize === 100 ? 'Про' : selectedPackageSize === 200 ? 'Бизнес' : `${formatNumber(selectedPackageSize || 0)} лидов`;
    const checkoutTotal = annualMode ? parseMoney(annualOffer.price) : getPackageTotal(selectedPrice);
    const annualPromoIneligible = annualMode && promoCode.trim().toUpperCase() === 'MANIA60';
    const promoDiscountAmount = !monthlyAllocationChange && !annualPromoIneligible && promoDiscount && !promoError && checkoutTotal > 0
        ? Math.min((checkoutTotal * promoDiscount) / 100, monthlyMode ? Math.max(0, checkoutTotal - 1) : checkoutTotal)
        : 0;
    const totalAfterPromo = Math.max(0, checkoutTotal - promoDiscountAmount);
    const maxBonusToUse = monthlyMode ? Math.max(0, Math.floor(totalAfterPromo - 1)) : totalAfterPromo;
    const actualBonusToUse = !monthlyAllocationChange && useBonusPoints && bonusPoints > 0
        ? Math.min(bonusPoints, maxBonusToUse) : 0;
    const finalTotal = Math.max(0, totalAfterPromo - actualBonusToUse);
    const isPurchaseDisabled =
        isPurchasing ||
        isLoading ||
        (!monthlyAllocationChange && isValidatingPromo) ||
        (!monthlyAllocationChange && Boolean(promoError)) ||
        annualPromoIneligible ||
        (purchaseMode === 'annual' && !annualOffer) ||
        (activeAnnualAccess && !annualMode) ||
        (monthlyMode && Boolean(subscription?.payment_processing)) ||
        (monthlyMode && subscription?.status === 'active' && selectedPackageSize === subscription.package_size) ||
        (!annualMode && (!selectedPackageSize || !selectedPrice));
    if (activeAnnualAccess && !annualOffer && !isLoading) {
        return <StreamSection id="checkout" title="Годовой безлимит">
            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14 }}>
                Доступ действует до {formatStreamDate(stream.access_expires_at)}. Продление для этой категории сейчас недоступно.
            </Typography>
        </StreamSection>;
    }
    return (
        <StreamSection
            id="checkout"
            title={pendingLaunch ? 'Запуск потока' : 'Пополнить поток'}
            summary={annualMode
                ? `Годовой безлимит · ${formatCurrency(finalTotal, 2)}`
                : selectedPrice ? `${formatCurrency(parseMoney(selectedPrice.price_per_lead), 2)}/лид · ${formatCurrency(monthlyMode ? checkoutTotal : finalTotal, 2)}${monthlyMode ? '/мес' : ''}` : undefined}
            plain={pendingLaunch}
        >
            {isLoading ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={120}
                >
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {!pendingLaunch && (
                        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, border: `1px solid ${CARD_BORDER}`, borderRadius: '8px', bgcolor: 'var(--lm-surface)', px: 2, py: 1.5 }}>
                            <Box>
                                <Typography sx={{ color: 'var(--lm-text)', fontWeight: 700, fontSize: 14 }}>
                                    {hasActiveStreamAccess(stream)
                                        ? `Безлимит до ${formatStreamDate(stream.access_expires_at)}`
                                        : activeBalance > 0 ? `${formatNumber(activeBalance)} лидов осталось` : 'Лиды закончились'}
                                </Typography>
                                {!hasActiveStreamAccess(stream) && currentPlanName && <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mt: 0.25 }}>{monthlyAllocationChange ? 'Следующий месяц' : 'Последний пакет'}: {currentPlanName}</Typography>}
                            </Box>
                            {subscription?.status === 'active' && <Typography sx={{ color: 'var(--lm-accent-ink)', fontSize: 13, fontWeight: 700 }}>Автопополнение включено</Typography>}
                        </Box>
                    )}
                    {annualOffer && <StreamBillingChoice
                        annual={annualMode}
                        packagesLabel={pendingLaunch ? 'Пакеты лидов' : 'Докупить лиды'}
                        onPackages={() => onModeChange(subscription?.status === 'active' ? 'monthly' : 'one_time')}
                        onAnnual={() => onModeChange('annual')}
                        packagesDisabled={activeAnnualAccess}
                        annualDisabled={subscription?.status === 'active' || subscription?.status === 'pending'}
                    />}
                    {!annualMode && !pendingLaunch && subscription?.status === 'active' && (
                        <Box role="group" aria-label="Способ пополнения" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                            {([
                                ['one_time', 'Докупить сейчас'],
                                ['monthly', 'Изменить следующий месяц'],
                            ] as const).map(([mode, label]) => <Button key={mode} type="button" disableRipple variant="outlined" aria-pressed={purchaseMode === mode} onClick={() => onModeChange(mode)} sx={{ minHeight: 44, borderRadius: '8px', borderColor: purchaseMode === mode ? 'var(--lm-accent-ink)' : 'var(--lm-border)', color: purchaseMode === mode ? 'var(--lm-accent-ink)' : 'var(--lm-text)', bgcolor: purchaseMode === mode ? 'var(--lm-accent-soft)' : 'var(--lm-surface)', fontWeight: purchaseMode === mode ? 700 : 500, textTransform: 'none', '&:hover': { borderColor: 'var(--lm-accent-ink)', bgcolor: purchaseMode === mode ? 'var(--lm-accent-soft)' : 'var(--lm-hover)' } }}>{label}</Button>)}
                        </Box>
                    )}
                    {subscription && (
                        <Box sx={{ ...cardSx, p: 1.5 }}>
                            <Typography sx={{ fontWeight: 700, color: 'var(--lm-text)' }}>
                                {subscription.status === 'active'
                                    ? `Следующий месяц: ${formatNumber(subscription.package_size)} лидов`
                                    : subscription.status === 'pending'
                                    ? 'Первый платёж проверяется'
                                    : subscription.status === 'cancelled'
                                    ? 'Ежемесячная оплата отключена'
                                    : 'Ежемесячная оплата остановлена'}
                            </Typography>
                            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mt: 0.5 }}>
                                {formatCurrency(parseMoney(subscription.price_per_lead), 2)}/лид · {formatCurrency(parseMoney(subscription.total_price), 2)}/мес
                                {subscription.next_charge_at ? ` · Следующее списание ${dayjs(subscription.next_charge_at).format('DD.MM.YYYY')}` : ''}
                            </Typography>
                            {subscription.payment_processing && (
                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mt: 0.5 }}>
                                    Списание проверяется. Изменить или отключить следующий месяц можно после результата.
                                </Typography>
                            )}
                            {subscription.status === 'pending' && paymentLink(subscription.confirmation_url) && (
                                <Button
                                    onClick={() => { window.location.href = paymentLink(subscription.confirmation_url); }}
                                    sx={{ mt: 0.75, textTransform: 'none' }}
                                >
                                    Продолжить оплату
                                </Button>
                            )}
                            {subscription.status === 'past_due' && (
                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mt: 0.5 }}>
                                    Остаток лидов сохранён. Для продолжения выберите объём и оплатите снова.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {!annualMode && !activeAnnualAccess && <StreamPackagePicker
                        prices={displayPrices}
                        customOnly={!pendingLaunch}
                        selectedSize={annualMode ? null : selectedPackageSize}
                        currentSize={!pendingLaunch && activeBalance > 0 ? currentPlanSize : null}
                        onSelect={(size) => { if (annualMode) onModeChange('one_time'); onPackageSelect(size); }}
                        disabled={isPurchasing || (monthlyMode && Boolean(subscription?.payment_processing))}
                        monthly={monthlyMode}
                    />}
                    {annualMode && annualOffer && (
                        <AnnualUnlimitedCard
                            price={formatCurrency(parseMoney(annualOffer.price), 2)}
                            comparisonPrice={parseMoney(annualOffer.metadata?.comparison_total) > 0
                                ? formatCurrency(parseMoney(annualOffer.metadata?.comparison_total), 2) : undefined}
                            discountPercent={Number(annualOffer.metadata?.prepayment_discount_percent) || undefined}
                            selected
                            disabled={isPurchasing || subscription?.status === 'active' || subscription?.status === 'pending'}
                            onClick={() => onModeChange('annual')}
                        />
                    )}
                    {annualOffer && (subscription?.status === 'active' || subscription?.status === 'pending') && (
                        <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>
                            Чтобы перейти на годовой тариф, сначала отключите автопополнение.
                        </Typography>
                    )}
                    {annualMode && (
                        <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>
                            Количество лидов не гарантируется. Вы получаете все найденные лиды, которые пройдут персональный ИИ-фильтр.
                        </Typography>
                    )}
                    {!annualMode && !pendingLaunch && activeBalance > 0 && currentPlanSize && selectedPackageSize && selectedPackageSize > currentPlanSize && unitSaving > 0 && (
                        <Typography sx={{ color: 'var(--lm-success-ink)', fontSize: 13, fontWeight: 700 }}>
                            {monthlyAllocationChange ? `Со следующего месяца ${selectedPlanName}` : `Докупка ${selectedPlanName}`}: цена ниже на {formatCurrency(unitSaving, 2)} за лид.
                        </Typography>
                    )}
                    {!annualMode && !hasActiveStreamAccess(stream) && monthlyStatus.available && subscription?.status !== 'pending' && subscription?.status !== 'active' && (
                        <AutoTopUpChoice
                            checked={monthlyMode}
                            onChange={(checked) => onModeChange(checked ? 'monthly' : 'one_time')}
                            oneTimeUnit={oneTimeUnit}
                            monthlyUnit={monthlyUnit}
                            packageSize={selectedPackageSize || 0}
                            disabled={isPurchasing || isCancellingMonthly || Boolean(subscription?.payment_processing)}
                        />
                    )}
                    {subscription?.status === 'active' && (
                        <Button type="button" variant="text" onClick={onCancelMonthly} disabled={isCancellingMonthly || Boolean(subscription.payment_processing)}
                            sx={{ alignSelf: 'flex-start', color: 'var(--lm-muted)', textTransform: 'none', px: 0 }}>
                            Отключить автопополнение
                        </Button>
                    )}
                    {!annualMode && <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>
                        Неиспользованные за месяц лиды сохраняются.
                    </Typography>}

                    <Stack spacing={1.5}>
                        <FormControlLabel
                            labelPlacement="start"
                            sx={{
                                border: `1px solid ${CARD_BORDER}`,
                                borderRadius: '8px',
                                m: 0,
                                minHeight: 56,
                                px: 1.2,
                                bgcolor: 'var(--lm-surface)',
                                justifyContent: 'space-between',
                                '& .MuiFormControlLabel-label': {
                                    color: 'var(--lm-text)',
                                    fontSize: 14,
                                    fontWeight: 500,
                                },
                            }}
                            control={
                                <Switch
                                    checked={useBonusPoints}
                                    disabled={bonusPoints <= 0 || monthlyAllocationChange}
                                    onChange={(event) => onUseBonusPointsChange(event.target.checked)}
                                    sx={{
                                        color: 'var(--lm-hover)',
                                        '&.Mui-checked': {
                                            color: 'var(--lm-accent-ink)',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ color: 'var(--lm-text)', fontSize: 14, fontWeight: 600 }}>Использовать баллы</Typography>
                                    <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>{bonusPoints > 0 ? formatNumber(bonusPoints) : 'Нет баллов'}</Typography>
                                </Box>
                            }
                        />
                        <Box>
                            <PromoTicketInput
                                value={promoCode}
                                onChange={onPromoCodeChange}
                                disabled={monthlyAllocationChange}
                                valid={Boolean(promoDiscount) && !promoError && !annualPromoIneligible}
                            />
                            {(isValidatingPromo || annualPromoIneligible || promoError || promoDiscount) && (
                                <Typography sx={{ mt: 0.5, fontSize: 12, fontWeight: 500, color: promoError || annualPromoIneligible ? 'error.main' : 'var(--lm-success-ink)' }}>
                                    {isValidatingPromo ? 'Проверяем' : annualPromoIneligible ? 'MANIA60 действует только на пакеты лидов' : promoError || `Скидка ${promoDiscount}%`}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    {monthlyAllocationChange && selectedPackageSize !== subscription?.package_size && (
                        <Box sx={{ ...cardSx, p: 1.5 }}>
                            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>
                                Сейчас: {formatNumber(subscription?.package_size || 0)} лидов · {formatCurrency(parseMoney(subscription?.total_price), 2)}/мес
                            </Typography>
                            <Typography sx={{ color: 'var(--lm-text)', fontSize: 14, fontWeight: 700, mt: 0.5 }}>
                                Со следующего списания: {formatNumber(selectedPackageSize || 0)} лидов · {formatCurrency(checkoutTotal, 2)}/мес
                            </Typography>
                            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mt: 0.5 }}>
                                Сегодня списания нет.
                            </Typography>
                            <Box sx={{ mt: 1, overflow: 'hidden', borderRadius: '8px' }}>
                                <LeadPackageCashback
                                    packageSize={selectedPackageSize || 0}
                                    paidAmount={checkoutTotal}
                                    monthlyAmount={checkoutTotal}
                                    futureOnly
                                />
                            </Box>
                        </Box>
                    )}

                    {!monthlyAllocationChange && checkoutTotal > 0 && (
                        <Box
                            sx={{
                                border: `1px solid ${CARD_BORDER}`,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                bgcolor: 'var(--lm-surface)',
                            }}
                        >
                            <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${CARD_BORDER}` }}>
                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, fontWeight: 600, mb: 1.5 }}>
                                    {annualMode ? 'Годовой безлимит' : monthlyMode ? 'Лиды каждый месяц' : 'Разовая покупка'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ color: 'var(--lm-text)', fontSize: 14, fontWeight: 700 }}>
                                            {annualMode
                                                ? 'Все подходящие лиды в течение 365 дней'
                                                : monthlyMode
                                                ? `${formatNumber(selectedPackageSize || 0)} новых лидов каждый месяц`
                                                : `Поток: ${formatNumber(selectedPackageSize || 0)} целевых лидов`}
                                        </Typography>
                                        <Typography sx={{ color: 'var(--lm-muted)', fontSize: 12, fontWeight: 600, mt: 0.4 }}>
                                            Ниша: {stream.category.name}
                                        </Typography>
                                        {!annualMode && <Typography sx={{ color: 'var(--lm-text)', fontSize: 12, fontWeight: 700 }}>
                                            {formatCurrency(parseMoney(selectedPrice?.price_per_lead), 2)}/лид
                                        </Typography>}
                                    </Box>
                                    <Typography sx={{ color: 'var(--lm-text)', fontSize: 20, fontWeight: 800, whiteSpace: 'nowrap' }}>
                                        {formatCurrency(checkoutTotal, 2)}
                                    </Typography>
                                </Box>
                            </Box>
                            {promoDiscountAmount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 2, py: 1, borderBottom: `1px solid ${CARD_BORDER}` }}>
                                    <Typography sx={{ color: 'var(--lm-success-ink)', fontSize: 13 }}>
                                        Промокод {promoCode.trim()} {promoDiscount}%
                                    </Typography>
                                    <Typography sx={{ color: 'var(--lm-success-ink)', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        −{formatCurrency(promoDiscountAmount, 2)}
                                    </Typography>
                                </Box>
                            )}
                            {actualBonusToUse > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 2, py: 1, borderBottom: `1px solid ${CARD_BORDER}` }}>
                                    <Typography sx={{ color: 'var(--lm-success-ink)', fontSize: 13 }}>Баллы</Typography>
                                    <Typography sx={{ color: 'var(--lm-success-ink)', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        −{formatCurrency(actualBonusToUse, 2)}
                                    </Typography>
                                </Box>
                            )}
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    px: 2,
                                    py: 2,
                                }}
                            >
                                <Typography
                                    color="text.primary"
                                    sx={{ fontSize: 20, fontWeight: 800 }}
                                >
                                    К оплате сегодня
                                </Typography>
                                <Typography
                                    color="text.primary"
                                    sx={{ fontSize: 20, fontWeight: 800, whiteSpace: 'nowrap' }}
                                >
                                    {formatCurrency(finalTotal, 2)}
                                </Typography>
                            </Box>
                            {!annualMode && <LeadPackageCashback
                                packageSize={selectedPackageSize || 0}
                                paidAmount={finalTotal}
                                monthlyAmount={monthlyMode ? checkoutTotal : undefined}
                            />}
                        </Box>
                    )}

                    {activePrices.length === 0 && (
                        <Alert
                            severity="warning"
                            sx={{ borderRadius: '8px' }}
                        >
                            Покупка для этого потока пока недоступна
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onPurchase}
                        disabled={isPurchaseDisabled}
                        sx={{ borderRadius: '8px', minHeight: 56, textTransform: 'none', fontWeight: 600, fontSize: 16 }}
                    >
                        {isPurchasing ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    sx={{ color: 'inherit', mr: 1 }}
                                />
                                Открываем оплату
                            </>
                        ) : monthlyMode && subscription?.status === 'active' ? (
                            selectedPackageSize === subscription.package_size
                                ? 'Текущий объём'
                                : `Сохранить ${formatNumber(selectedPackageSize || 0)} лидов на следующий месяц — ${formatCurrency(checkoutTotal, 2)}/мес`
                        ) : monthlyMode && (promoDiscountAmount > 0 || actualBonusToUse > 0) ? (
                            `Оплатить сегодня ${formatCurrency(finalTotal, 2)} и включить автопополнение`
                        ) : monthlyMode ? (
                            `Получать ${formatNumber(selectedPackageSize || 0)} лидов каждый месяц — ${formatCurrency(checkoutTotal, 2)}/мес`
                        ) : annualMode ? (
                            `Оплатить годовой безлимит — ${formatCurrency(finalTotal, 2)}`
                        ) : pendingLaunch ? (
                            finalTotal <= 0 ? (
                                'Активировать за баллы'
                            ) : (
                                `Оплатить ${formatCurrency(finalTotal, 2)}`
                            )
                        ) : (
                            `Пополнить поток на ${formatNumber(selectedPackageSize || 0)} лидов`
                        )}
                    </Button>
                </Stack>
            )}
        </StreamSection>
    );
}

function StreamActionsPanel({
    stream,
    isUpdating,
    isRefreshing,
    onReload,
    onToggleActive,
    onFocusCheckout,
}: {
    stream: Stream;
    isUpdating: boolean;
    isRefreshing: boolean;
    onReload: () => void;
    onToggleActive: () => void;
    onFocusCheckout: () => void;
}) {
    const history = useHistory();
    const status = getStreamStatus(stream);
    return (
        <Box
            role="group"
            aria-label="Управление потоком"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '& .MuiButton-root': {
                    minHeight: 34,
                    px: 1.5,
                    fontSize: 13,
                    borderRadius: '8px',
                    textTransform: 'none',
                },
            }}
        >
            <Button
                variant="contained"
                disabled={isUpdating || isRefreshing}
                onClick={status.key === 'paused'
                    ? onToggleActive
                    : status.key === 'active' && getStreamApprovedDeliveries(stream) > 0
                    ? () => history.push(`/feed?stream=${stream.id}`)
                    : onFocusCheckout}
            >
                {isUpdating ? 'Сохраняем…' : getMainActionLabel(stream)}
            </Button>
            {status.key === 'active' && (
                <Button
                    variant="outlined"
                    disabled={isUpdating || isRefreshing}
                    onClick={onToggleActive}
                >
                    На паузу
                </Button>
            )}
            {status.key === 'paused' && (
                <Button
                    variant="outlined"
                    onClick={onFocusCheckout}
                >
                    Пополнить
                </Button>
            )}
            <Tooltip title="Обновить данные">
                <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                    <IconButton
                        aria-label="Обновить данные"
                        disabled={isRefreshing || isUpdating}
                        onClick={onReload}
                        sx={{ width: 34, height: 34, color: 'var(--lm-muted)' }}
                    >
                        {isRefreshing ? (
                            <CircularProgress size={17} />
                        ) : (
                            <IconRefresh
                                size={18}
                                stroke={1.5}
                            />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}

export default function LeadStreamDetailsScreen() {
    const { streamId } = useParams<{ streamId: string }>();
    const history = useHistory();
    const { search, hash } = useLocation();
    const isMobile = useIsMobileSize();
    const { enqueueSnackbar } = useSnackbar();
    const { bonusPoints } = useUserStore(({ bonusPoints }) => ({ bonusPoints }));
    const navigationRef = useRef<StreamNavigationHandle | null>(null);
    const [stream, setStream] = useState<Stream | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const refreshing = useRef(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedPackageSize, setSelectedPackageSize] = useState<number | null>(null);
    const packageSelectionTouched = useRef(false);
    const [purchaseMode, setPurchaseMode] = useState<'monthly' | 'one_time' | 'annual'>('one_time');
    const [isCancellingMonthly, setIsCancellingMonthly] = useState(false);
    const [useBonusPoints, setUseBonusPoints] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoDiscount, setPromoDiscount] = useState<number | null>(null);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);

    const numericStreamId = streamIdFromPath(streamId) || 0;
    useEffect(() => {
        if (numericStreamId && /^\d+$/.test(streamId)) {
            history.replace(streamDetailsPath(numericStreamId, `${search}${hash}`));
        }
    }, [history, numericStreamId, streamId, search, hash]);
    const categoryId = stream?.category.id;
    const visibleStreamId = useRef<number | null>(numericStreamId);
    useEffect(() => {
        visibleStreamId.current = numericStreamId;
        packageSelectionTouched.current = false;
        refreshing.current = false;
        setIsRefreshing(false);
        return () => {
            visibleStreamId.current = null;
        };
    }, [numericStreamId]);

    const loadStream = useCallback(async () => {
        if (!Number.isFinite(numericStreamId) || numericStreamId <= 0) {
            setError('Поток не найден');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const data = await LeadStreamsService.getStream(numericStreamId);
            if (getStreamStatus(data).key === 'pending') {
                history.replace(`${ROUTES_URL.LEADSTREAM}?stream=${data.id}`);
                return;
            }
            if (!shouldOpenStreamDetails(data)) {
                history.replace(ROUTES_URL.LEADSTREAM);
                return;
            }

            setStream(data);
        } catch (_requestError) {
            setError('Не удалось загрузить поток');
        } finally {
            setIsLoading(false);
        }
    }, [history, numericStreamId]);

    useEffect(() => {
        void loadStream();
    }, [loadStream]);

    useEffect(() => {
        let live = true;
        const subscription = EventService.subscribe<Stream>('LEAD_STREAMS_UPDATED', (_type, updated) => {
            if (updated?.id === numericStreamId) setStream(updated);
            else if (!updated) {
                void LeadStreamsService.getStream(numericStreamId)
                    .then((data) => {
                        if (live) setStream(data);
                    })
                    .catch(() => {
                        if (live) enqueueSnackbar('Не удалось обновить данные потока', { variant: 'warning' });
                    });
            }
        });
        return () => {
            live = false;
            subscription.unsubscribe();
        };
    }, [numericStreamId, enqueueSnackbar]);

    const performanceDateFrom = useMemo(
        () =>
            dayjs()
                .subtract(PERFORMANCE_CALENDAR_DAYS - 1, 'day')
                .format('YYYY-MM-DD'),
        []
    );
    const performanceDateTo = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

    const {
        data: dailyLeadsResponse,
        isLoading: isPerformanceLoading,
        isError: isPerformanceError,
        refetch: reloadPerformance,
    } = useQuery({
        queryKey: ['lead-stream-performance', numericStreamId, performanceDateFrom, performanceDateTo],
        queryFn: () =>
            getDailyLeadsStats({
                from: performanceDateFrom,
                to: performanceDateTo,
                streams: String(numericStreamId),
            }),
        enabled: !!stream && getStreamStatus(stream).key !== 'pending' && Number.isFinite(numericStreamId),
        onError: () => {
            enqueueSnackbar('Не удалось загрузить статистику потока', { variant: 'error' });
        },
    });

    const { data: categoryPrices = [], isLoading: isPricesLoading } = useQuery({
        queryKey: ['lead-stream-detail-prices', categoryId],
        queryFn: () => (categoryId ? LeadStreamsService.getCategoryPrices(categoryId) : Promise.resolve([])),
        enabled: !!categoryId,
        onError: () => {
            enqueueSnackbar('Не удалось загрузить пакеты лидов', { variant: 'error' });
        },
    });

    const { data: categoryOffers = [], isLoading: isOffersLoading } = useQuery({
        queryKey: ['lead-stream-detail-offers', categoryId],
        queryFn: () => (categoryId ? LeadStreamsService.getCategoryOffers(categoryId) : Promise.resolve([])),
        enabled: !!categoryId,
    });
    const annualOffer = categoryOffers.find((offer) => offer.code === 'leadsmania-annual-unlimited-12m') || null;

    useEffect(() => {
        if (stream && hasActiveStreamAccess(stream) && annualOffer) setPurchaseMode('annual');
    }, [stream, annualOffer]);

    const { data: monthlyStatus = { available: false, subscription: null }, isLoading: isMonthlyLoading, refetch: reloadMonthlyStatus } = useQuery({
        queryKey: ['lead-stream-monthly', numericStreamId],
        queryFn: () => LeadStreamsService.getMonthlyLeadStatus(numericStreamId),
        enabled: Number.isFinite(numericStreamId) && numericStreamId > 0,
    });

    const { data: allPurchases = [], isLoading: isPurchasesLoading, isError: isPurchasesError, refetch: reloadPurchases } = useQuery({
        queryKey: ['lead-stream-purchases', numericStreamId],
        queryFn: () => LeadStreamsService.getPurchases(),
        enabled: !!stream && Number.isFinite(numericStreamId) && numericStreamId > 0,
    });
    const streamPurchases = useMemo(() => purchasesForStream(allPurchases, numericStreamId), [allPurchases, numericStreamId]);
    const latestPaidPurchase = streamPurchases.find((purchase) => purchase.status === 'paid' && purchase.purchase_type === 'package') || null;
    const currentPackageSize = latestPaidPurchase?.package_size || monthlyStatus.subscription?.package_size || stream?.fixed_package_size || null;

    const activePrices = useMemo(
        () => categoryPrices.filter((price) => price.is_active).sort((a, b) => a.package_size - b.package_size),
        [categoryPrices]
    );

    useEffect(() => {
        const defaultPrice =
            activePrices.find((price) => price.package_size === (monthlyStatus.subscription?.status === 'active' ? monthlyStatus.subscription.package_size : null)) ||
            activePrices.find((price) => price.package_size === latestPaidPurchase?.package_size) ||
            activePrices.find((price) => price.package_size === stream?.fixed_package_size) ||
            activePrices.find((price) => price.package_size === 100) ||
            activePrices[0];

        setSelectedPackageSize((current) =>
            !packageSelectionTouched.current && defaultPrice
                ? defaultPrice.package_size
                : activePrices.some((price) => price.package_size === current) ? current : defaultPrice?.package_size || null
        );
    }, [activePrices, latestPaidPurchase?.package_size, monthlyStatus.subscription?.package_size, monthlyStatus.subscription?.status, stream?.fixed_package_size]);

    useEffect(() => {
        const nextSize = monthlyStatus.subscription?.package_size;
        if (monthlyStatus.subscription?.status === 'active' && nextSize &&
            activePrices.some((price) => price.package_size === nextSize)) {
            setSelectedPackageSize(nextSize);
            setPurchaseMode('monthly');
        }
    }, [activePrices, monthlyStatus.subscription?.package_size, monthlyStatus.subscription?.status]);

    const handleToggleActive = useCallback(async () => {
        if (!stream) {
            return;
        }

        setIsUpdating(true);
        try {
            const updated = await LeadStreamsService.updateStream(stream.id, { is_active: !stream.is_active });
            setStream(updated);
            EventService.dispatch('LEAD_STREAMS_UPDATED', updated);
            enqueueSnackbar(updated.is_active ? 'Поток запущен' : 'Поток поставлен на паузу', { variant: 'success' });
        } catch (_requestError) {
            enqueueSnackbar('Не удалось изменить статус потока', { variant: 'error' });
        } finally {
            setIsUpdating(false);
        }
    }, [enqueueSnackbar, stream]);

    const handleFocusCheckout = useCallback(() => {
        navigationRef.current?.openSection('checkout');
    }, []);

    useEffect(() => {
        const normalizedPromoCode = promoCode.trim();
        if (!normalizedPromoCode) {
            setPromoDiscount(null);
            setPromoError(null);
            setIsValidatingPromo(false);
            return undefined;
        }

        setIsValidatingPromo(true);
        setPromoError(null);

        const timer = window.setTimeout(() => {
            SubscriptionsService.checkPromocode(normalizedPromoCode, stream?.id)
                .then((response) => {
                    if (response?.discount_percent) {
                        setPromoDiscount(response.discount_percent);
                        setPromoError(null);
                        return;
                    }

                    setPromoDiscount(null);
                    setPromoError('Недействительный промокод');
                })
                .catch((promoRequestError: any) => {
                    setPromoDiscount(null);
                    setPromoError(promoRequestError?.error || 'Недействительный промокод');
                })
                .finally(() => {
                    setIsValidatingPromo(false);
                });
        }, 500);

        return () => window.clearTimeout(timer);
    }, [promoCode, stream?.id]);

    const handlePurchase = useCallback(async () => {
        if (!stream) {
            return;
        }

        const monthlyPurchase = !hasActiveStreamAccess(stream) && monthlyStatus.available && purchaseMode === 'monthly' &&
            monthlyStatus.subscription?.status !== 'pending';
        const purchaseData: {
            package_size?: number;
            offer_id?: string;
            promo_code?: string;
            use_bonus_points?: boolean;
            use_wallet_rub: boolean;
            auto_renewal?: boolean;
            request_key?: string;
        } = {
            use_wallet_rub: !monthlyPurchase,
            auto_renewal: monthlyPurchase,
        };

        if (purchaseMode === 'annual') {
            if (!annualOffer) {
                enqueueSnackbar('Годовой тариф для этой категории пока недоступен', { variant: 'warning' });
                return;
            }
            purchaseData.offer_id = annualOffer.code;
        } else if (!selectedPackageSize) {
            enqueueSnackbar('Выберите пакет лидов', { variant: 'warning' });
            return;
        } else {
            purchaseData.package_size = selectedPackageSize;
        }

        if (monthlyPurchase && monthlyStatus.subscription?.status === 'active' && selectedPackageSize !== null) {
            setIsPurchasing(true);
            try {
                await LeadStreamsService.changeMonthlyLeadAllocation(stream.id, selectedPackageSize);
                await reloadMonthlyStatus();
                enqueueSnackbar('Новый объём сохранён для следующего месяца', { variant: 'success' });
            } catch (error: any) {
                enqueueSnackbar(error?.response?.data?.error || 'Не удалось изменить объём', { variant: 'error' });
            } finally {
                setIsPurchasing(false);
            }
            return;
        }

        const normalizedPromoCode = promoCode.trim();
        if (normalizedPromoCode && promoDiscount && !promoError) {
            purchaseData.promo_code = normalizedPromoCode;
        }

        if (useBonusPoints && bonusPoints > 0) {
            purchaseData.use_bonus_points = true;
        }

        setIsPurchasing(true);
        try {
            const ownerId = useUserStore.getState().id;
            purchaseData.request_key = packageRequestKey(ownerId, stream.id, purchaseData);
            const response = await LeadStreamsService.purchasePackage(stream.id, purchaseData);
            if (response.confirmation_url) {
                rememberPackageCheckout(useUserStore.getState().id, [{ streamId: stream.id, response }]);
                window.location.href = response.confirmation_url;
                return;
            }

            if (response.paid_immediately) {
                clearPackageRequestKey(ownerId, stream.id);
                enqueueSnackbar('Поток пополнен', { variant: 'success' });
                await loadStream();
                void reloadPurchases();
                EventService.dispatch('LEAD_STREAMS_UPDATED');
                return;
            }

            enqueueSnackbar('Покупка создана, но ссылка на оплату не получена', { variant: 'warning' });
        } catch (purchaseError: any) {
            if (monthlyPurchase) void reloadMonthlyStatus();
            enqueueSnackbar(
                purchaseError?.response?.data?.error ||
                    purchaseError?.response?.data?.message ||
                    'Не удалось открыть оплату',
                { variant: 'error' }
            );
        } finally {
            setIsPurchasing(false);
        }
    }, [
        annualOffer,
        bonusPoints,
        enqueueSnackbar,
        loadStream,
        monthlyStatus,
        promoCode,
        promoDiscount,
        promoError,
        purchaseMode,
        reloadMonthlyStatus,
        reloadPurchases,
        selectedPackageSize,
        stream,
        useBonusPoints,
    ]);

    const handleCancelMonthly = useCallback(async () => {
        if (!stream || !window.confirm('Отключить ежемесячную оплату? Остаток лидов сохранится.')) return;
        setIsCancellingMonthly(true);
        try {
            await LeadStreamsService.cancelMonthlyLeadSubscription(stream.id);
            await reloadMonthlyStatus();
            setPurchaseMode('one_time');
            enqueueSnackbar('Ежемесячная оплата отключена', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.error || 'Не удалось отключить ежемесячную оплату', { variant: 'error' });
        } finally {
            setIsCancellingMonthly(false);
        }
    }, [enqueueSnackbar, reloadMonthlyStatus, stream]);

    const handleRefresh = async () => {
        if (!stream || refreshing.current || isUpdating) return;
        refreshing.current = true;
        setIsRefreshing(true);
        try {
            const [fresh] = await Promise.all([
                LeadStreamsService.getStream(numericStreamId),
                getStreamStatus(stream).key !== 'pending' ? reloadPerformance() : Promise.resolve(),
                reloadMonthlyStatus(),
            ]);
            if (visibleStreamId.current !== numericStreamId) return;
            setStream(fresh);
            EventService.dispatch('LEAD_STREAMS_UPDATED', fresh);
        } catch {
            if (visibleStreamId.current === numericStreamId)
                enqueueSnackbar('Не удалось обновить поток. Попробуйте ещё раз.', { variant: 'error' });
        } finally {
            if (visibleStreamId.current === numericStreamId) {
                refreshing.current = false;
                setIsRefreshing(false);
            }
        }
    };

    const usagePercent = getUsagePercent(stream);
    const performanceStats = dailyLeadsResponse?.data?.data || [];
    const performanceRows = getPerformanceRows(performanceStats, DEFAULT_PERFORMANCE_PERIOD_DAYS);
    const performanceCalendarRows = getPerformanceRows(performanceStats, PERFORMANCE_CALENDAR_DAYS);
    const runoutForecast = isPerformanceLoading
        ? 'Считаем прогноз'
        : isPerformanceError
        ? 'Прогноз временно недоступен'
        : getRunoutForecast(stream, performanceRows);
    const isCheckoutLoading = isPricesLoading || isOffersLoading || isMonthlyLoading;

    const content = (
        <Box
            sx={{
                pb: { xs: 4, sm: 5 },
            }}
        >
            {isLoading ? (
                <LeadStreamDetailsSkeleton />
            ) : error || !stream ? (
                <Box sx={{ maxWidth: 720 }}>
                    <Alert
                        severity="error"
                        sx={{ borderRadius: CARD_RADIUS }}
                    >
                        {error || 'Поток не найден'}
                    </Alert>
                    <Button
                        onClick={() => history.push(ROUTES_URL.LEADSTREAM)}
                        sx={{ mt: 2, textTransform: 'none', fontWeight: 500 }}
                    >
                        Вернуться к запуску потока
                    </Button>
                </Box>
            ) : (
                <Stack spacing={2}>
                    <StickyStreamHeader>
                        <UsageOverviewCard
                            stream={stream}
                            currentPackageSize={currentPackageSize}
                            usagePercent={usagePercent}
                            forecast={runoutForecast}
                        >
                            <StreamActionsPanel
                                stream={stream}
                                isUpdating={isUpdating}
                                isRefreshing={isRefreshing}
                                onReload={() => void handleRefresh()}
                                onToggleActive={handleToggleActive}
                                onFocusCheckout={handleFocusCheckout}
                            />
                        </UsageOverviewCard>
                        {isMobile && <StreamSectionNavigation mobile />}
                    </StickyStreamHeader>

                    {getStreamStatus(stream).key !== 'pending' && (
                        <PerformanceChart
                            calendarRows={performanceCalendarRows}
                            recentRows={performanceRows}
                            isLoading={isPerformanceLoading}
                            error={isPerformanceError}
                            onRetry={() => void reloadPerformance()}
                        />
                    )}

                    <StreamManagerFunnel
                        key={`manager-${stream.id}`}
                        stream={stream}
                    />
                    <StreamWorkspaceSections
                        key={stream.id}
                        stream={stream}
                        onUpdated={setStream}
                        showTraining={getStreamStatus(stream).key !== 'pending' && (getStreamPackageSize(stream) > 0 || hasActiveStreamAccess(stream))}
                    />

                    <Box>
                        <StreamCheckoutPanel
                            activePrices={activePrices}
                            annualOffer={annualOffer}
                            bonusPoints={bonusPoints}
                            isLoading={isCheckoutLoading}
                            isPurchasing={isPurchasing}
                            isValidatingPromo={isValidatingPromo}
                            monthlyStatus={monthlyStatus}
                            latestPaidPurchase={latestPaidPurchase}
                            purchaseMode={purchaseMode}
                            onModeChange={setPurchaseMode}
                            onCancelMonthly={handleCancelMonthly}
                            isCancellingMonthly={isCancellingMonthly}
                            onPackageSelect={(size) => { packageSelectionTouched.current = true; setSelectedPackageSize(size); }}
                            onPromoCodeChange={setPromoCode}
                            onPurchase={handlePurchase}
                            onUseBonusPointsChange={setUseBonusPoints}
                            promoCode={promoCode}
                            promoDiscount={promoDiscount}
                            promoError={promoError}
                            selectedPackageSize={selectedPackageSize}
                            stream={stream}
                            useBonusPoints={useBonusPoints}
                        />
                    </Box>
                    {getStreamStatus(stream).key !== 'pending' && (
                        <>
                            <StreamPurchaseHistory purchases={streamPurchases} loading={isPurchasesLoading} error={isPurchasesError} />
                        </>
                    )}
                </Stack>
            )}
        </Box>
    );

    const sections: PageSection[] = [
        ...(stream && getStreamStatus(stream).key !== 'pending'
            ? [{ id: 'performance' as const, title: 'Статистика' }]
            : []),
        { id: 'funnel', title: 'Воронка продаж' },
        { id: 'rules', title: 'Настройка потока' },
        {
            id: 'checkout',
            title: stream && getStreamStatus(stream).key === 'pending' ? 'Запуск потока' : 'Пополнить поток',
        },
        ...(stream && getStreamStatus(stream).key !== 'pending'
            ? [
                { id: 'purchases' as const, title: 'История покупок' },
            ]
            : []),
    ];
    return (
        <StreamPageSections
            key={streamId}
            sections={sections}
            defaultCollapsed={{ rules: true, checkout: true }}
            navigationRef={navigationRef}
            initialSection={
                (['#knowledge', '#business', '#training'].includes(hash) ? 'rules' : hash.slice(1)) ||
                (new URLSearchParams(search).get('checkout') === '1' ? 'checkout' : undefined)
            }
        >
            {isMobile ? (
                <LayoutPaper>{content}</LayoutPaper>
            ) : (
                <DesktopLayout
                    rightSidebarWidth={220}
                    rightSidebar={
                        !isLoading && stream ? (
                            <Box sx={{ pt: 5 }}>
                                <StreamSectionNavigation />
                            </Box>
                        ) : undefined
                    }
                    hideRightSidebar={isLoading || !stream}
                >
                    {content}
                </DesktopLayout>
            )}
        </StreamPageSections>
    );
}
